import React from 'react';

import type { WidgetProps } from '@rjsf/utils';

import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import { REGIONS_COUNTRY_CODES } from '@luxuryescapes/lib-regions/lib/phoneNumberValidation';

interface PhonePrefixOption {
  countryCode: string;
  label: string;
  regionCode: string;
  value: string;
}

const PREFERRED_REGION_CODES = ['AU', 'NZ', 'GB', 'SG'];
const MERGED_REGION_CODES = ['US', 'CA'];

const sortedRegionCodes = [
  ...PREFERRED_REGION_CODES,
  ...Object.keys(REGIONS_COUNTRY_CODES)
    .filter((regionCode) => !PREFERRED_REGION_CODES.includes(regionCode) && !MERGED_REGION_CODES.includes(regionCode))
    .sort((firstRegionCode, secondRegionCode) =>
      REGIONS_COUNTRY_CODES[firstRegionCode].countryName.localeCompare(
        REGIONS_COUNTRY_CODES[secondRegionCode].countryName,
      ),
    ),
];

export const PHONE_PREFIX_OPTIONS: Array<PhonePrefixOption> = sortedRegionCodes.map((regionCode) => {
  const region = REGIONS_COUNTRY_CODES[regionCode];

  return {
    countryCode: region.countryCode,
    label: `(+${region.countryCode}) ${region.countryName}`,
    regionCode,
    value: region.countryCode,
  };
});

PHONE_PREFIX_OPTIONS.splice(2, 0, {
  countryCode: '1',
  label: '(+1) United States of America / Canada',
  regionCode: 'US',
  value: '1',
});

export function filterPhonePrefixOptions(options: Array<PhonePrefixOption>, search: string) {
  const query = search.trim().toLocaleLowerCase();
  if (!query) {
    return options;
  }

  return options.filter((option) =>
    [option.regionCode, option.countryCode, option.label].some((value) => value.toLocaleLowerCase().includes(query)),
  );
}

function getFlagEmoji(regionCode: string) {
  return String.fromCodePoint(
    ...regionCode
      .toUpperCase()
      .split('')
      .map((character) => 127397 + character.charCodeAt(0)),
  );
}

export default function PhonePrefixWidget({
  disabled,
  id,
  label,
  onChange,
  rawErrors,
  readonly,
  required,
  value,
}: WidgetProps) {
  const selectedOption = PHONE_PREFIX_OPTIONS.find((option) => option.value === value) ?? null;

  return (
    <Autocomplete
      autoHighlight
      disabled={disabled || readonly}
      filterOptions={(options, state) => filterPhonePrefixOptions(options, state.inputValue)}
      getOptionLabel={(option) => option.label}
      id={id}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      noOptionsText="No matching country or code"
      onChange={(_event, option) => onChange(option?.value ?? '', undefined, id)}
      openOnFocus
      options={PHONE_PREFIX_OPTIONS}
      renderInput={(params) => (
        <TextField
          {...params}
          error={Boolean(rawErrors?.length)}
          label={label}
          required={required}
          placeholder="Search country or code"
        />
      )}
      renderOption={(optionProps, option) => (
        <Box component="li" {...optionProps} key={option.value}>
          <Stack alignItems="center" direction="row" spacing={1}>
            <Typography component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
              {getFlagEmoji(option.regionCode)}
            </Typography>
            <Typography variant="body2">{option.label}</Typography>
          </Stack>
        </Box>
      )}
      value={selectedOption}
    />
  );
}
